import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Video, Mic, Square, Trash2, Play, Pause } from "lucide-react";
import { cn } from "@/lib/utils";

interface MediaRecorderWidgetProps {
  onRecorded: (blob: Blob, type: "video" | "audio") => void;
  onClear: () => void;
  recordedUrl: string | null;
  recordedType: "video" | "audio" | null;
  disabled?: boolean;
}

export default function MediaRecorderWidget({
  onRecorded,
  onClear,
  recordedUrl,
  recordedType,
  disabled,
}: MediaRecorderWidgetProps) {
  const [mode, setMode] = useState<"idle" | "video" | "audio">("idle");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const audioPlaybackRef = useRef<HTMLAudioElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const MAX_DURATION = 60; // seconds

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setElapsed(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startRecording = async (type: "video" | "audio") => {
    try {
      const constraints: MediaStreamConstraints =
        type === "video"
          ? { video: { facingMode: "user", width: 720, height: 720 }, audio: true }
          : { audio: true };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (type === "video" && videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }

      const mimeType = type === "video"
        ? (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm")
        : (MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm");

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        onRecorded(blob, type);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
      };

      recorder.start(250);
      setMode(type);
      setRecording(true);
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev + 1 >= MAX_DURATION) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    } catch {
      // Permission denied or no device
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const handleClear = () => {
    cleanup();
    setMode("idle");
    onClear();
  };

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // Show recorded preview
  if (recordedUrl && recordedType) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {recordedType === "video" ? "🎥 Video recorded" : "🎙️ Audio recorded"}
          </span>
          <button
            onClick={handleClear}
            disabled={disabled}
            className="text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>

        {recordedType === "video" ? (
          <video
            src={recordedUrl}
            controls
            className="w-full rounded-xl border border-border max-h-48 bg-black"
          />
        ) : (
          <div className="flex items-center gap-3 bg-muted/50 rounded-xl p-3 border border-border">
            <button
              onClick={() => {
                const el = audioPlaybackRef.current;
                if (!el) return;
                if (playing) { el.pause(); setPlaying(false); }
                else { el.play(); setPlaying(true); }
              }}
              className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0"
            >
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <div className="flex-1 h-2 bg-border rounded-full overflow-hidden">
              <div className="h-full bg-primary/40 rounded-full w-full" />
            </div>
            <audio
              ref={audioPlaybackRef}
              src={recordedUrl}
              onEnded={() => setPlaying(false)}
              className="hidden"
            />
          </div>
        )}
      </div>
    );
  }

  // Recording in progress
  if (recording) {
    return (
      <div className="space-y-3">
        {mode === "video" && (
          <video
            ref={videoPreviewRef}
            muted
            playsInline
            className="w-full rounded-xl border border-border max-h-48 bg-black mirror"
            style={{ transform: "scaleX(-1)" }}
          />
        )}

        {mode === "audio" && (
          <div className="flex items-center justify-center gap-3 bg-muted/50 rounded-xl p-6 border border-border">
            <div className="w-4 h-4 rounded-full bg-destructive animate-pulse" />
            <span className="text-sm font-medium text-foreground">Recording…</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm tabular-nums text-muted-foreground">
            {formatTime(elapsed)} / {formatTime(MAX_DURATION)}
          </span>
          <Button
            onClick={stopRecording}
            variant="destructive"
            size="sm"
            className="rounded-full gap-2"
          >
            <Square size={14} /> Stop
          </Button>
        </div>
      </div>
    );
  }

  // Idle – show record buttons
  return (
    <div className="flex gap-2">
      <button
        onClick={() => startRecording("video")}
        disabled={disabled}
        className={cn(
          "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-border",
          "text-sm font-medium text-foreground hover:bg-muted/50 transition-colors",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <Video size={16} className="text-muted-foreground" /> Video
      </button>
      <button
        onClick={() => startRecording("audio")}
        disabled={disabled}
        className={cn(
          "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-border",
          "text-sm font-medium text-foreground hover:bg-muted/50 transition-colors",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <Mic size={16} className="text-muted-foreground" /> Audio
      </button>
    </div>
  );
}
